import { io, Socket } from 'socket.io-client';
import { getFreshAccessToken } from '@/utils/authFetch';
import { API_BASE_URL } from '../config';

export type WebSocketChannel = 'balance' | 'positions' | 'signals' | 'prices';

export interface BalanceUpdateEvent {
  type: 'balance_update';
  timestamp: string;
  balance: {
    balance: number;
    mode: string;
    currency?: string;
  };
}

export interface PositionUpdateEvent {
  type: 'position_update';
  timestamp: string;
  positions: any[];
  total_pnl?: number;
  count?: number;
  metadata?: Record<string, unknown>;
}

export interface SignalUpdateEvent {
  type: 'signal_update';
  timestamp: string;
  signal: any;
}

export interface PriceUpdateEvent {
  type: 'price_update';
  timestamp: string;
  symbol: string;
  price: any;
}

type EventPayload =
  | BalanceUpdateEvent
  | PositionUpdateEvent
  | SignalUpdateEvent
  | PriceUpdateEvent;

type EventName = EventPayload['type'];

type EventHandler<T extends EventPayload> = (payload: T) => void;
type SocketEventHandler = (...args: any[]) => void;

const MAX_RECONNECT_ATTEMPTS = 5;
const INITIAL_RECONNECT_DELAY = 1000;
const IS_DEV_ENV = process.env.NODE_ENV !== 'production';

export class WebSocketService {
  private socket: Socket | null = null;
  private isConnecting = false;
  private isConnected = false;
  private reconnectAttempts = 0;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private channelRefCounts: Map<WebSocketChannel, number> = new Map();
  private activeConsumers = 0;
  private listeners: Map<string, Set<SocketEventHandler>> = new Map();
  private shouldReconnect = false;
  private internalHandlers: {
    connect?: () => void;
    connectError?: (error: Error) => void;
    disconnect?: (reason: string) => void;
    error?: (error: Error) => void;
  } = {};

  get connected(): boolean {
    return this.isConnected;
  }

  get connecting(): boolean {
    return this.isConnecting;
  }

  acquireConsumer(): number {
    this.activeConsumers += 1;
    return this.activeConsumers;
  }

  releaseConsumer(): number {
    if (this.activeConsumers > 0) {
      this.activeConsumers -= 1;
    }
    return this.activeConsumers;
  }

  async connect(token: string): Promise<void> {
    if (this.isConnected) {
      return;
    }

    if (this.isConnecting) {
      return new Promise<void>((resolve, reject) => {
        const checkStatus = () => {
          if (this.isConnected) {
            resolve();
          } else if (!this.isConnecting) {
            reject(new Error('WebSocket connection failed'));
          } else {
            setTimeout(checkStatus, 100);
          }
        };
        checkStatus();
      });
    }

    this.isConnecting = true;
    this.shouldReconnect = true;
    this.clearReconnectTimer();

    return new Promise((resolve, reject) => {
      // Connect to the namespace URL directly
      // Backend expects: namespace='/api/ws/stream' with path='/api/ws/socket.io'
      const url = `${API_BASE_URL}/api/ws/stream`;

      this.socket = io(url, {
        auth: {
          token,
        },
        path: '/api/ws/socket.io',
        withCredentials: true,
        reconnection: false,
        // Polling first: sync gthread workers cannot hold websocket upgrades;
        // polling connects reliably and upgrades opportunistically when the
        // server supports it (eventlet). Avoids the expected
        // "closed before established" console error on websocket-first dial.
        transports: ['polling', 'websocket'],
      });

      this.attachAllListeners();

      const handleConnect = () => {
        this.isConnected = true;
        this.isConnecting = false;
        this.reconnectAttempts = 0;
        this.clearReconnectTimer();
        const activeChannels = Array.from(this.channelRefCounts.entries())
          .filter(([, count]) => count > 0)
          .map(([channel]) => channel);
        if (activeChannels.length > 0) {
          this.emitSubscribe(activeChannels);
        }
        resolve();
      };

      const handleConnectError = (error: Error) => {
        // Only log in development mode to avoid exposing sensitive info
        if (IS_DEV_ENV) {
          console.error('[WebSocketService] Connection error:', error.message);
        }
        this.isConnecting = false;
        this.scheduleReconnect();
        reject(error);
      };

      const handleDisconnect = (reason: string) => {
        // Only log in development mode
        if (IS_DEV_ENV) {
          console.warn('[WebSocketService] Disconnected:', reason);
        }
        this.isConnected = false;
        this.isConnecting = false;
        this.scheduleReconnect();
      };

      const handleError = (error: Error) => {
        if (IS_DEV_ENV) {
          console.error('[WebSocketService] Socket error:', error.message || error);
        }
      };

      this.internalHandlers = {
        connect: handleConnect,
        connectError: handleConnectError,
        disconnect: handleDisconnect,
        error: handleError,
      };

      this.socket.once('connect', handleConnect);
      this.socket.once('connect_error', handleConnectError);
      this.socket.on('disconnect', handleDisconnect);

      // Generic error logging (development only)
      this.socket.on('error', handleError);
    });
  }

  disconnect(): void {
    this.shouldReconnect = false;
    this.clearReconnectTimer();

    if (this.socket) {
      this.detachInternalListeners(this.socket);
      this.detachAllListeners();
      this.socket.disconnect();
      this.socket = null;
    }

    this.isConnected = false;
    this.isConnecting = false;
    this.reconnectAttempts = 0;
    if (this.activeConsumers === 0) {
      this.channelRefCounts.clear();
    }
  }

  subscribe(channels: WebSocketChannel[]): void {
    const channelsToEmit: WebSocketChannel[] = [];

    channels.forEach((channel) => {
      const currentCount = this.channelRefCounts.get(channel) ?? 0;
      const nextCount = currentCount + 1;
      this.channelRefCounts.set(channel, nextCount);
      if (currentCount === 0) {
        channelsToEmit.push(channel);
      }
    });

    if (channelsToEmit.length === 0) {
      return;
    }

    this.emitSubscribe(channelsToEmit);
  }

  unsubscribe(channels: WebSocketChannel[]): void {
    const channelsToEmit: WebSocketChannel[] = [];

    channels.forEach((channel) => {
      const currentCount = this.channelRefCounts.get(channel) ?? 0;
      if (currentCount <= 1) {
        this.channelRefCounts.delete(channel);
        channelsToEmit.push(channel);
      } else {
        this.channelRefCounts.set(channel, currentCount - 1);
      }
    });

    if (channelsToEmit.length === 0) {
      return;
    }

    this.emitUnsubscribe(channelsToEmit);
  }

  onBalanceUpdate(callback: EventHandler<BalanceUpdateEvent>): () => void {
    return this.addTypedListener('balance_update', callback);
  }

  onPositionUpdate(callback: EventHandler<PositionUpdateEvent>): () => void {
    return this.addTypedListener('position_update', callback);
  }

  onSignalUpdate(callback: EventHandler<SignalUpdateEvent>): () => void {
    return this.addTypedListener('signal_update', callback);
  }

  onPriceUpdate(callback: EventHandler<PriceUpdateEvent>): () => void {
    return this.addTypedListener('price_update', callback);
  }

  onConnect(callback: () => void): () => void {
    return this.addSocketListener('connect', callback);
  }

  onDisconnect(callback: (reason?: string) => void): () => void {
    return this.addSocketListener('disconnect', (reason?: string) => callback(reason));
  }

  onConnectError(callback: (err: Error) => void): () => void {
    return this.addSocketListener('connect_error', (err: Error) => callback(err));
  }

  requestUpdate(): void {
    if (!this.socket || !this.isConnected) {
      return;
    }
    this.socket.emit('request_update');
  }

  ping(): void {
    if (!this.socket || !this.isConnected) {
      return;
    }
    this.socket.emit('ping');
  }

  private addTypedListener<T extends EventPayload>(event: EventName, callback: EventHandler<T>): () => void {
    return this.addSocketListener(event, callback as SocketEventHandler);
  }

  private addSocketListener(event: string, handler: SocketEventHandler): () => void {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set());
    }
    const handlers = this.listeners.get(event)!;
    handlers.add(handler);

    if (this.socket) {
      this.socket.on(event, handler);
    }

    return () => {
      if (this.socket) {
        this.socket.off(event, handler);
      }
      handlers.delete(handler);
      if (handlers.size === 0) {
        this.listeners.delete(event);
      }
    };
  }

  private attachAllListeners(): void {
    if (!this.socket) {
      return;
    }

    this.listeners.forEach((handlers, event) => {
      handlers.forEach((handler) => {
        this.socket?.on(event, handler);
      });
    });
  }

  private detachAllListeners(): void {
    if (!this.socket) {
      return;
    }

    this.listeners.forEach((handlers, event) => {
      handlers.forEach((handler) => {
        this.socket?.off(event, handler);
      });
    });
  }

  private clearReconnectTimer(): void {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  }

  private detachInternalListeners(socket: Socket): void {
    if (this.internalHandlers.connect) {
      socket.off('connect', this.internalHandlers.connect);
    }
    if (this.internalHandlers.connectError) {
      socket.off('connect_error', this.internalHandlers.connectError);
    }
    if (this.internalHandlers.disconnect) {
      socket.off('disconnect', this.internalHandlers.disconnect);
    }
    if (this.internalHandlers.error) {
      socket.off('error', this.internalHandlers.error);
    }
    this.internalHandlers = {};
  }

  private scheduleReconnect(): void {
    if (!this.shouldReconnect || this.activeConsumers === 0) {
      return;
    }

    if (this.reconnectAttempts >= MAX_RECONNECT_ATTEMPTS) {
      if (IS_DEV_ENV) {
        console.error('[WebSocketService] Max reconnect attempts reached');
      }
      return;
    }

    this.clearReconnectTimer();

    const delay = INITIAL_RECONNECT_DELAY * Math.pow(2, this.reconnectAttempts);
    this.reconnectAttempts += 1;

    if (IS_DEV_ENV) {
      console.info(`[WebSocketService] Scheduling reconnect attempt ${this.reconnectAttempts} in ${delay}ms`);
    }
    this.reconnectTimer = setTimeout(async () => {
      if (!this.shouldReconnect || this.activeConsumers === 0) {
        return;
      }

      if (this.socket) {
        this.detachInternalListeners(this.socket);
        this.detachAllListeners();
        this.socket.removeAllListeners();
        this.socket = null;
      }

      const token = await getFreshAccessToken();
      if (!token) {
        if (IS_DEV_ENV) {
          console.error('[WebSocketService] Reconnect failed: no authentication token available');
        }
        return;
      }

      this.connect(token).catch((error) => {
        if (IS_DEV_ENV) {
          console.error('[WebSocketService] Reconnect failed:', error.message);
        }
      });
    }, delay);
  }

  private emitSubscribe(channels: WebSocketChannel[]): void {
    if (!this.socket || !this.isConnected) {
      return;
    }
    this.socket.emit('subscribe', { channels });
  }

  private emitUnsubscribe(channels: WebSocketChannel[]): void {
    if (!this.socket || !this.isConnected) {
      return;
    }
    this.socket.emit('unsubscribe', { channels });
  }
}

export const websocketService = new WebSocketService();
export default websocketService;
